import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Grid, Typography, alpha, useTheme } from '@mui/material';

import {
    ticketDepartmentSelector,
    ticketIdSelector,
    ticketPrioritySelector,
    ticketInfoSelector,
    ticketStatusSelector,
    ticketTitleSelector,
    useAppSelector,
} from '../../../app/store';
import TicketInfoChip from './ticket-info-chip';

const TicketContainer = ({ children }: any) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const ticketTitle = useAppSelector(ticketTitleSelector);
    const ticketId = useAppSelector(ticketIdSelector);
    const ticketDepartment = useAppSelector(ticketDepartmentSelector);
    const ticketStatus = useAppSelector(ticketStatusSelector);
    const ticketPriority = useAppSelector(ticketPrioritySelector);
    const ticketInfo = useAppSelector(ticketInfoSelector);
    const ticketGatewayTitle = ticketInfo?.gateway?.title;

    const path = window.location.pathname;
    const handleGoBack = () => {
        navigate(path.split(ticketId ?? '')[0]);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}>
            <Box
                sx={{
                    px: { xs: 1.5, md: 2.25 },
                    py: 1.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexShrink: 0,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Button
                        onClick={handleGoBack}
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            width: 38,
                            height: 38,
                            minWidth: 38,
                            p: 0,
                            borderRadius: 2,
                            color:
                                theme.palette.mode === 'dark'
                                    ? theme.palette.primary.light
                                    : theme.palette.primary.main,
                            borderColor:
                                theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.light, 0.34)
                                    : alpha(theme.palette.primary.main, 0.28),
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.light, 0.07)
                                    : 'transparent',
                            '&:hover': {
                                borderColor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.light, 0.55)
                                        : theme.palette.primary.main,
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.light, 0.13)
                                        : alpha(theme.palette.primary.main, 0.06),
                            },
                        }}
                        variant="outlined">
                        <i className="fa-light fa-chevron-right" />
                    </Button>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: 'YekanBalkhHeavy',
                                fontSize: { xs: 16, md: 18 },
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {ticketTitle}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', mt: 0.25, display: 'block' }}>
                            شناسه تیکت {ticketId}
                        </Typography>
                    </Box>
                </Box>

                <Grid
                    container
                    spacing={0.75}
                    sx={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        flexWrap: 'wrap',
                    }}>
                    <TicketInfoChip label="دپارتمان" value={ticketDepartment} />
                    {ticketGatewayTitle && (
                        <TicketInfoChip
                            label="درگاه"
                            value={ticketGatewayTitle}
                            rawValue
                        />
                    )}
                    <TicketInfoChip label="اولویت" value={ticketPriority} />
                    <TicketInfoChip label="وضعیت" value={ticketStatus} />
                </Grid>
            </Box>

            <CardContent
                sx={{
                    p: '0 !important',
                    width: '100%',
                    display: 'flex',
                    flexGrow: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.default',
                }}>
                {children}
            </CardContent>
        </Card>
    );
};

export default memo(TicketContainer);
